import { Description } from '@mui/icons-material';
import { Box, Paper, Typography } from '@mui/material';
import EnhancedTable from 'lib/designSystem/Table';
import Title from 'lib/designSystem/Title';
import React from 'react';

import { OperativeProfileData } from '../index';

const Documents = ({ data }: { data: OperativeProfileData }) => {
  const hasDocuments =
    data?.factoringDocumentsAsClient?.length ||
    data?.factoringDocumentsAsStakeholder?.length ||
    data?.creditDocuments?.length ||
    data?.confirmingDocuments?.length ||
    data?.pendingAccountsInFavor?.length ||
    data?.pendingAccountsAgainst?.length ||
    data?.sgrDocuments?.length;
  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
      }}
    >
      <Title
        title="Documentos"
        icon={<Description color="primary" sx={{ fontSize: 20 }} />}
        color="primary"
      />
      {!hasDocuments && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={4}
          color="text.secondary"
        >
          <Description sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6">No hay documentos disponibles</Typography>
          <Typography variant="body2">
            No se encontraron documentos para mostrar
          </Typography>
        </Box>
      )}
      {data?.factoringDocumentsAsClient?.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}
          >
            Factoring (cliente)
          </Typography>
          <EnhancedTable
            data={data?.factoringDocumentsAsClient}
            dataHeaders={[
              {
                key: 'stakeholderName',
                label: 'Deudor',
              },
              {
                key: 'stakeholderIdentifier',
                label: 'Rut deudor',
              },
              { key: 'folio', label: 'Folio' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountConciliated',
                label: 'Conciliado',
                type: 'money',
              },
              {
                key: 'documentRemainingAmountNet',
                label: 'Adeudado doc',
                type: 'money',
              },
              {
                key: 'arrearAmount',
                label: 'Mora',
                type: 'money',
              },
              {
                key: 'totalRemainingNet',
                label: 'Adeudado total',
                type: 'invoice-remaining-amount',
              },
              { key: 'issuedDate', label: 'Emisión', type: 'date' },
              {
                key: 'receiptDate',
                label: 'Recepción',
                type: 'eight-days-date',
              },
              { key: 'paymentDate', label: 'Vencimiento', type: 'date' },
              {
                key: 'arrearTrench',
                label: 'Tramo de mora',
                type: 'arrear-trench',
              },
              {
                key: 'arrearDays',
                label: 'Días de mora',
              },
              { key: 'fundName', label: 'Fondo' },
              { key: 'cancelDate', label: 'Rechazo', type: 'date' },
              { key: 'confirmDate', label: 'Confirmación', type: 'date' },
              { key: 'operationId', label: 'Solicitud', type: 'numeric-id' },
              {
                key: 'operationDate',
                label: 'Fecha solicitud',
                type: 'date',
              },
            ]}
            exportToFile="xlsx"
            finalSumRow={true}
          />
        </>
      )}
      {data?.factoringDocumentsAsStakeholder?.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}
          >
            Factoring (deudor)
          </Typography>
          <EnhancedTable
            data={data?.factoringDocumentsAsStakeholder}
            dataHeaders={[
              {
                key: 'businessName',
                label: 'Cliente',
              },
              {
                key: 'businessIdentifier',
                label: 'Rut cliente',
              },
              { key: 'folio', label: 'Folio' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountConciliated',
                label: 'Conciliado',
                type: 'money',
              },
              {
                key: 'documentRemainingForStakeholder',
                label: 'Adeudado total',
                type: 'invoice-remaining-amount',
              },
              { key: 'issuedDate', label: 'Emisión', type: 'date' },
              {
                key: 'receiptDate',
                label: 'Recepción',
                type: 'eight-days-date',
              },
              { key: 'paymentDate', label: 'Vencimiento', type: 'date' },
              {
                key: 'arrearTrench',
                label: 'Tramo de mora',
                type: 'arrear-trench',
              },
              {
                key: 'arrearDays',
                label: 'Días de mora',
              },
              { key: 'cancelDate', label: 'Rechazo', type: 'date' },
              { key: 'confirmDate', label: 'Confirmación', type: 'date' },
            ]}
            exportToFile="xlsx"
            finalSumRow={true}
          />
        </>
      )}
      {data?.sgrDocuments?.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}
          >
            Garantías
          </Typography>
          <EnhancedTable
            data={data?.sgrDocuments}
            dataHeaders={[
              { key: 'folio', label: 'Folio' },
              {
                key: 'businessName',
                label: 'Cliente',
              },
              {
                key: 'businessIdentifier',
                label: 'Rut cliente',
              },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountConciliated',
                label: 'Conciliado',
                type: 'money',
              },
              { key: 'issuedDate', label: 'Emisión', type: 'date' },
            ]}
            exportToFile="xlsx"
            finalSumRow={true}
          />
        </>
      )}

      {data?.creditDocuments?.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}
          >
            Créditos
          </Typography>
          <EnhancedTable
            data={data?.creditDocuments}
            dataHeaders={[
              { key: 'folio', label: 'Folio' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountConciliated',
                label: 'Conciliado',
                type: 'money',
              },
              {
                key: 'documentRemainingAmountNet',
                label: 'Adeudado doc',
                type: 'money',
              },
              {
                key: 'arrearAmount',
                label: 'Mora',
                type: 'money',
              },
              {
                key: 'totalRemainingNet',
                label: 'Adeudado total',
                type: 'invoice-remaining-amount',
              },
              { key: 'issuedDate', label: 'Emisión', type: 'date' },
              { key: 'paymentDate', label: 'Vencimiento', type: 'date' },
              {
                key: 'arrearTrench',
                label: 'Tramo de mora',
                type: 'arrear-trench',
              },
              {
                key: 'arrearDays',
                label: 'Días de mora',
              },
              { key: 'operationId', label: 'Solicitud', type: 'numeric-id' },
              {
                key: 'operationDate',
                label: 'Fecha Solicitud',
                type: 'date',
              },
            ]}
            exportToFile="xlsx"
            finalSumRow={true}
          />
        </>
      )}
      {data?.confirmingDocuments?.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}
          >
            Confirming
          </Typography>
          <EnhancedTable
            data={data?.confirmingDocuments}
            dataHeaders={[
              {
                key: 'businessName',
                label: 'Cliente',
              },
              {
                key: 'businessIdentifier',
                label: 'Rut cliente',
              },
              { key: 'folio', label: 'Folio' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountConciliated',
                label: 'Conciliado',
                type: 'money',
              },
              {
                key: 'documentRemainingAmountNet',
                label: 'Adeudado doc',
                type: 'money',
              },
              {
                key: 'arrearAmount',
                label: 'Mora',
                type: 'money',
              },
              {
                key: 'totalRemainingNet',
                label: 'Adeudado total',
                type: 'invoice-remaining-amount',
              },
              { key: 'issuedDate', label: 'Emisión', type: 'date' },
              {
                key: 'receiptDate',
                label: 'Recepción',
                type: 'eight-days-date',
              },
              { key: 'paymentDate', label: 'Vencimiento', type: 'date' },
              {
                key: 'arrearTrench',
                label: 'Tramo de mora',
                type: 'arrear-trench',
              },
              {
                key: 'arrearDays',
                label: 'Días de mora',
              },
              { key: 'fundName', label: 'Fondo' },
              { key: 'cancelDate', label: 'Rechazo', type: 'date' },
              { key: 'confirmDate', label: 'Confirmación', type: 'date' },
              { key: 'operationId', label: 'Solicitud', type: 'numeric-id' },
              {
                key: 'operationDate',
                label: 'Fecha Solicitud',
                type: 'date',
              },
            ]}
            exportToFile="xlsx"
            finalSumRow={true}
          />
        </>
      )}
      {data?.pendingAccountsInFavor?.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}
          >
            Cuentas por pagar
          </Typography>
          <EnhancedTable
            data={data?.pendingAccountsInFavor}
            dataHeaders={[
              { key: 'folio', label: 'Id', type: 'numeric-id' },
              { key: 'pendingAccountType', label: 'Categoría' },
              { key: 'pendingAccountObservation', label: 'Observación' },
              { key: 'issuedDate', label: 'Fecha', type: 'date' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'pendingAccountInFavorRemainingAmount',
                label: 'Monto por pagar',
                type: 'money',
              },
            ]}
            exportToFile="xlsx"
            finalSumRow={true}
          />
        </>
      )}
      {data?.pendingAccountsAgainst?.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}
          >
            Cuentas por cobrar
          </Typography>
          <EnhancedTable
            data={data?.pendingAccountsAgainst}
            dataHeaders={[
              { key: 'folio', label: 'Id', type: 'numeric-id' },
              { key: 'pendingAccountType', label: 'Categoría' },
              { key: 'pendingAccountObservation', label: 'Observación' },
              { key: 'issuedDate', label: 'Fecha', type: 'date' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'documentRemainingAmountNet',
                label: 'Monto por cobrar',
                type: 'money',
              },
            ]}
            exportToFile="xlsx"
            finalSumRow={true}
          />
        </>
      )}
    </Paper>
  );
};

export default Documents;
